import React, { useEffect } from 'react';
import { useGate, useStore } from 'effector-react';
import { LibApiType, type LibItemMedia, type LibItemSysIcon } from '@types';
import { queryClient, queryKeys } from '@queryClient';
import Title from '@components/Title';
import Button from '@components/Button';
import { useLibRecent } from '../utils/libRecent';
import { LibItemsGate, LibMultipleGate } from '../stores/libState';
import libReset from '../actions/libReset';
import { $libSelected } from '../stores/libSelected';
import LibModal from '../layouts/LibModal';
import SearchBar from '../components/SearchBar';
import UploadButtonMedia from '../components/UploadButtonMedia';
import FooterClear from '../components/FooterClear';
import LibModalNav from '../layouts/LibModalNav';
import onAlbumClick from '../actions/onAlbumClick';
import LibModalSpace from '../layouts/LibModalSpace';
import TipImage from '../LibImageModal/TipImage';
import ListImage from '../LibImageModal/ListImage';
import { LibCBIcons } from './LibCBIcons';

type Props = {
  multiple?: boolean;
  onConfirm: (result: {
    libItems?: LibItemMedia[];
    sysItems?: LibItemSysIcon[];
  }) => void;
  onRequestClose: () => void;
};

export function LibCBIconModal({
  multiple = false,
  onConfirm,
  onRequestClose,
}: Props) {
  const [curNav, setCurNav] = useLibRecent<'lib' | 'sys'>(
    'LibImageModal',
    'sys'
  );

  useGate(LibMultipleGate, multiple);
  useEffect(() => libReset, []);
  const noSelected = !useStore($libSelected).length;

  const handleConfirm = () => {
    const selected = $libSelected.getState();
    if (curNav === 'sys') {
      const items = (LibItemsGate.state.getState() as LibItemSysIcon[]).filter(
        (x, i) => selected.includes(i)
      );
      onConfirm({ sysItems: items });
    } else if (curNav === 'lib') {
      const items = (LibItemsGate.state.getState() as LibItemMedia[])
        .filter((x) => selected.includes(x.id))
        .sort((a, b) => selected.indexOf(a.id) - selected.indexOf(b.id));
      onConfirm({ libItems: items });
    }
  };

  return (
    <LibModal
      header={
        <>
          <Title>图标素材库</Title>
          {curNav === 'lib' && <SearchBar />}
          {curNav === 'lib' && (
            <UploadButtonMedia
              className="ml-auto"
              libApiType={LibApiType.image}
              onUploadDone={() => {
                queryClient.refetchQueries([queryKeys.libListImage, 'items']);
              }}
            />
          )}
        </>
      }
      footer={
        <>
          <Button disabled={noSelected} onClick={handleConfirm}>
            确认操作
          </Button>
          <FooterClear />
        </>
      }
      onRequestClose={onRequestClose}
    >
      <LibModalNav
        navItems={
          [
            { id: 'sys', name: '系统图标' },
            { id: 'lib', name: '个人图片' },
          ] as const
        }
        selected={curNav}
        onChange={(navType) => {
          onAlbumClick(0);
          setCurNav(navType);
        }}
      >
        {curNav === 'lib' && <LibModalSpace />}
        {curNav === 'lib' && <TipImage />}
      </LibModalNav>
      {curNav === 'lib' && <ListImage />}
      {curNav === 'sys' && <LibCBIcons />}
    </LibModal>
  );
}
